import { useState } from "react";
import { Phone, Mail, MapPin, Navigation, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { postCallback } from "../lib/api";
import { BUSINESS, mapsDirUrl, mapsEmbedUrl, mapsSearchUrl } from "../config/business";
import SEO from "../components/SEO";
import { FadeUp, MaskedLine } from "../components/anim";

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please enter your name and phone number.");
      return;
    }
    setSending(true);
    try {
      await postCallback(form);
      toast.success("Thanks! We'll call you back shortly.");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setSending(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div data-testid="contact-page">
      <SEO
        title={`Contact ${BUSINESS.name} | Smog Check Modesto, CA`}
        description={`Contact Smog Hut Modesto at ${BUSINESS.phoneDisplay} or ${BUSINESS.email}. Located at ${BUSINESS.address.full}. Request a callback online.`}
        keywords="contact smog check Modesto, smog station phone Modesto"
        path="/contact"
      />
      <section className="bg-white aurora relative overflow-hidden">
        <div className="section relative z-10 py-14">
          <p className="kicker">Contact</p>
          <h1 className="mt-2 font-head font-extrabold text-4xl sm:text-5xl text-ink">
            <MaskedLine lines={[{ text: "Get In Touch" }]} />
          </h1>
          <p className="mt-4 text-ink/70 font-body max-w-2xl">Call, email or request a callback — we're happy to answer any smog check questions.</p>
        </div>
      </section>

      <section className="section py-14 grid lg:grid-cols-3 gap-6">
        <FadeUp>
          <a href={`tel:${BUSINESS.phoneTel}`} className="card-soft p-6 block hover:shadow-volt transition-shadow">
            <span className="grid h-11 w-11 place-items-center rounded-xl bg-volt-dim text-volt"><Phone className="h-5 w-5" /></span>
            <h3 className="mt-4 font-head font-bold text-ink">Call Us</h3>
            <p className="text-sm text-volt font-semibold">{BUSINESS.phoneDisplay}</p>
          </a>
        </FadeUp>
        <FadeUp delay={0.05}>
          <a href={`mailto:${BUSINESS.email}`} className="card-soft p-6 block hover:shadow-volt transition-shadow">
            <span className="grid h-11 w-11 place-items-center rounded-xl bg-volt-dim text-volt"><Mail className="h-5 w-5" /></span>
            <h3 className="mt-4 font-head font-bold text-ink">Email Us</h3>
            <p className="text-sm text-volt font-semibold break-all">{BUSINESS.email}</p>
          </a>
        </FadeUp>
        <FadeUp delay={0.1}>
          <a href={mapsSearchUrl()} target="_blank" rel="noreferrer" className="card-soft p-6 block hover:shadow-volt transition-shadow">
            <span className="grid h-11 w-11 place-items-center rounded-xl bg-volt-dim text-volt"><MapPin className="h-5 w-5" /></span>
            <h3 className="mt-4 font-head font-bold text-ink">Visit Us</h3>
            <p className="text-sm text-ink/70">{BUSINESS.address.full}</p>
          </a>
        </FadeUp>
      </section>

      <section className="section pb-16 grid lg:grid-cols-2 gap-8 items-start">
        <FadeUp>
          <div className="card-soft p-7">
            <h2 className="font-head font-bold text-2xl text-ink">Request a Callback</h2>
            <p className="mt-1 text-sm text-ink/60 font-body">Leave your details and we'll get right back to you.</p>
            <form onSubmit={submit} className="mt-5 space-y-4">
              <input data-testid="contact-name" value={form.name} onChange={set("name")} placeholder="Your name *" className="w-full rounded-xl border border-line px-4 py-3 font-body text-sm focus:outline-none focus:ring-2 focus:ring-volt" />
              <input data-testid="contact-phone" value={form.phone} onChange={set("phone")} placeholder="Phone number *" className="w-full rounded-xl border border-line px-4 py-3 font-body text-sm focus:outline-none focus:ring-2 focus:ring-volt" />
              <input data-testid="contact-email" value={form.email} onChange={set("email")} placeholder="Email (optional)" className="w-full rounded-xl border border-line px-4 py-3 font-body text-sm focus:outline-none focus:ring-2 focus:ring-volt" />
              <textarea data-testid="contact-message" value={form.message} onChange={set("message")} placeholder="How can we help?" rows={4} className="w-full rounded-xl border border-line px-4 py-3 font-body text-sm focus:outline-none focus:ring-2 focus:ring-volt" />
              <button type="submit" disabled={sending} data-testid="contact-submit" className="btn-volt w-full disabled:opacity-60">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {sending ? "Sending..." : "Request Callback"}
              </button>
            </form>
          </div>
        </FadeUp>
        <FadeUp delay={0.1} className="space-y-4">
          <div className="rounded-2xl overflow-hidden border border-line shadow-sm">
            <iframe title="Map" src={mapsEmbedUrl()} className="w-full h-[340px]" loading="lazy" />
          </div>
          <a href={mapsDirUrl()} target="_blank" rel="noreferrer" className="btn-cta w-full"><Navigation className="h-4 w-4" /> Get Directions</a>
        </FadeUp>
      </section>
    </div>
  );
}
